import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import type { User } from '@bekebi/contracts/api';
import { ROLES, type Role } from '@bekebi/domain';
import { Button, buttonClass } from '../../components/button';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { useCurrentUser } from '../../session/current-user';
import { roleLabel } from '../../session/roles';
import { formatDateTime } from '../services/format';
import { useSendAccountLink, useUpdateUser, useUsers } from './api';
import { LinkIssueResult } from './link-result';

type Status = User['status'];
const STATUSES: readonly Status[] = ['active', 'disabled'];
export const STATUS_LABELS: Record<Status, string> = { active: 'Active', disabled: 'Disabled' };

/**
 * Users (/app/users): the people who can sign in to the portal, from GET /api/users (cursor
 * pagination). Each shows email, the system-generated Bekebi User ID (read-only), role (the
 * internal `operations` role is shown as ICT), status and last sign-in. Role and status are changed
 * through PATCH /api/users/:id after a confirmation; the API protects the last active Admin and
 * signs a changed user out everywhere. A user who has not chosen a password yet shows as Invited
 * (or Reset sent); the Admin can resend their link, or send a password reset link to someone who
 * has one (DECISIONS.md A-AC1). Nothing here is decided by the browser.
 */
export function UsersOverview() {
  const users = useUsers();
  const me = useCurrentUser();
  const location = useLocation();
  const created = (location.state as { notice?: string } | null)?.notice ?? null;
  const [saved, setSaved] = useState<string | null>(null);
  const all = users.data?.pages.flatMap((p) => p.items) ?? [];
  const notice = saved ?? created;

  const addLink = (
    <Link to="/app/users/new" className={buttonClass('primary')}>
      Add user
    </Link>
  );

  return (
    <div className="max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1>Users</h1>
        {addLink}
      </div>
      <p className="mt-1 text-sm text-muted">
        The people who can sign in to the Bekebi Portal. Each has a Bekebi User ID assigned by the
        portal. New users choose their own password from an emailed invitation. Changing
        someone&rsquo;s role or status signs them out everywhere and cancels any link they have not
        used.
      </p>
      {notice ? (
        <p role="status" className="mt-4 rounded-md bg-success-soft p-3 text-sm text-success">
          {notice}
        </p>
      ) : null}

      <div className="mt-6">
        {users.isPending ? (
          <LoadingState label="Loading users…" />
        ) : users.isError ? (
          <ErrorState
            title="Users could not be loaded"
            message={users.error.message}
            onRetry={() => void users.refetch()}
          />
        ) : all.length === 0 ? (
          <EmptyState
            title="No users yet."
            description="No portal users are recorded."
            action={addLink}
          />
        ) : (
          <>
            <div
              aria-hidden="true"
              className="hidden grid-cols-[1.6fr_1fr_0.6fr_0.7fr_1.1fr_auto] gap-4 px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted md:grid"
            >
              <span>User</span>
              <span>Bekebi User ID</span>
              <span>Role</span>
              <span>Status</span>
              <span>Last sign-in</span>
              <span className="w-20" />
            </div>
            <ul aria-label="Users" className="space-y-2">
              {all.map((user) => (
                <li key={user.id}>
                  <UserRow
                    user={user}
                    isSelf={user.id === me.id}
                    onSaved={(message) => setSaved(message)}
                    onEdit={() => setSaved(null)}
                  />
                </li>
              ))}
            </ul>
            {users.hasNextPage ? (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  onClick={() => void users.fetchNextPage()}
                  disabled={users.isFetchingNextPage}
                >
                  {users.isFetchingNextPage ? 'Loading…' : 'Show more users'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: Status }) {
  return status === 'active' ? (
    <span className="inline-flex rounded-full border border-success-line bg-success-soft px-2.5 py-0.5 text-xs font-semibold text-success">
      Active
    </span>
  ) : (
    <span className="inline-flex rounded-full border border-line-strong bg-raised px-2.5 py-0.5 text-xs font-semibold text-fg-2">
      Disabled
    </span>
  );
}

function UserRow({
  user,
  isSelf,
  onSaved,
  onEdit,
}: {
  user: User;
  isSelf: boolean;
  onSaved: (message: string) => void;
  onEdit: () => void;
}) {
  const [managing, setManaging] = useState(false);
  const panelId = useId();
  const manageRef = useRef<HTMLButtonElement>(null);

  return (
    <article className="rounded-lg border border-line bg-surface p-4 text-sm">
      <div className="grid gap-2 md:grid-cols-[1.6fr_1fr_0.6fr_0.7fr_1.1fr_auto] md:items-center md:gap-4">
        <div className="min-w-0">
          <h2 className="break-all text-sm font-semibold text-fg">{user.email}</h2>
          {isSelf ? <p className="text-xs text-muted">This is you</p> : null}
          <SetupNote user={user} />
        </div>
        <div>
          <span className="text-muted md:hidden">Bekebi User ID: </span>
          <span className="font-mono text-xs text-fg-2">{user.bekebiUserId}</span>
        </div>
        <div className="text-fg-2">
          <span className="text-muted md:hidden">Role: </span>
          {roleLabel(user.role)}
        </div>
        <div>
          <span className="sr-only">Status: </span>
          <StatusBadge status={user.status} />
        </div>
        <div className="text-fg-2">
          <span className="text-muted md:hidden">Last sign-in: </span>
          {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never signed in'}
        </div>
        <div className="md:w-20 md:text-right">
          <Button
            ref={manageRef}
            variant="secondary"
            aria-expanded={managing}
            aria-controls={managing ? panelId : undefined}
            aria-label={`Manage ${user.email}`}
            onClick={() => {
              onEdit();
              setManaging((open) => !open);
            }}
          >
            {managing ? 'Close' : 'Manage'}
          </Button>
        </div>
      </div>
      {managing ? (
        <div id={panelId} className="mt-4 border-t border-line pt-4">
          <ManageUserForm
            user={user}
            isSelf={isSelf}
            onDone={(message) => {
              setManaging(false);
              manageRef.current?.focus();
              if (message) onSaved(message);
            }}
          />
        </div>
      ) : null}
    </article>
  );
}

/** Whether the user can sign in yet, and the state of their newest link. Never the link itself. */
export function setupLabel(user: User): string | null {
  if (user.passwordSet) return null;
  const kind = user.accountLink?.purpose === 'password_reset' ? 'Reset link sent' : 'Invited';
  const state = user.accountLink?.state;
  if (state === 'open')
    return `${kind}: link works until ${formatDateTime(user.accountLink!.expiresAt)}`;
  if (state === 'expired') return `${kind}: link expired, resend it`;
  return `${kind}: no working link, resend it`;
}

function SetupNote({ user }: { user: User }) {
  const label = setupLabel(user);
  if (!label) return null;
  return (
    <p className="mt-1 inline-flex rounded-full border border-warning-line bg-warning-soft px-2.5 py-0.5 text-xs font-medium text-warning">
      {label}
    </p>
  );
}

/**
 * Sign-in links for one user. Someone without a password gets their link again (the old one stops
 * working). Someone with a password can be sent a reset link, which removes their password and
 * signs them out at once, so it is confirmed first. Your own password is changed from your
 * account menu instead.
 */
function SignInLinks({ user, isSelf }: { user: User; isSelf: boolean }) {
  const send = useSendAccountLink();
  const [confirming, setConfirming] = useState(false);
  if (user.status !== 'active') {
    return <p className="text-xs text-muted">Enable the account to send it a sign-in link.</p>;
  }
  if (isSelf) {
    return <p className="text-xs text-muted">Change your own password from your account menu.</p>;
  }
  const resend = !user.passwordSet;
  const kind = resend ? 'invitation' : 'password-reset';
  const label = resend
    ? user.accountLink?.purpose === 'password_reset'
      ? 'Resend reset link'
      : 'Resend invitation'
    : 'Send password reset link';
  const go = () =>
    send.mutate({ userId: user.id, kind }, { onSettled: () => setConfirming(false) });

  return (
    <div className="space-y-3">
      {send.isSuccess ? <LinkIssueResult email={user.email} result={send.data} /> : null}
      {send.isError ? (
        <p role="alert" className="text-sm text-danger">
          {send.error.message}
        </p>
      ) : null}
      {confirming ? (
        <div
          role="group"
          aria-label={`Confirm password reset for ${user.email}`}
          className="rounded-md border border-warning-line bg-warning-soft p-3"
        >
          <p className="text-warning">
            Reset {user.email}&rsquo;s password? Their current password stops working now, they are
            signed out everywhere, and they are emailed a link that works once, for one hour, to
            choose a new one.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button disabled={send.isPending} onClick={go}>
              {send.isPending ? 'Sending…' : 'Reset password and send link'}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Back
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="secondary"
          disabled={send.isPending}
          onClick={() => {
            send.reset();
            if (resend) go();
            else setConfirming(true);
          }}
        >
          {send.isPending ? 'Sending…' : label}
        </Button>
      )}
    </div>
  );
}

/** Role and status, confirmed before sending: saving signs the user out everywhere. */
function ManageUserForm({
  user,
  isSelf,
  onDone,
}: {
  user: User;
  isSelf: boolean;
  onDone: (message: string | null) => void;
}) {
  const update = useUpdateUser();
  const formId = useId();
  const [role, setRole] = useState<Role>(user.role);
  const [status, setStatus] = useState<Status>(user.status);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const backRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (confirming) backRef.current?.focus();
  }, [confirming]);

  const patch = {
    ...(role !== user.role ? { role } : {}),
    ...(status !== user.status ? { status } : {}),
  };
  const changes = [
    ...(patch.role ? [`role to ${roleLabel(patch.role)}`] : []),
    ...(patch.status ? [`status to ${STATUS_LABELS[patch.status]}`] : []),
  ];

  const review = () => {
    update.reset();
    if (changes.length === 0) {
      setNote('No changes to save.');
      return;
    }
    setNote(null);
    setConfirming(true);
  };

  const save = () =>
    update.mutate(
      { userId: user.id, ...patch },
      {
        onSuccess: (saved) =>
          onDone(
            `${saved.email} now has role ${roleLabel(saved.role)} and status ${STATUS_LABELS[saved.status]}. They were signed out and must sign in again.`,
          ),
      },
    );

  return (
    <div className="space-y-4" aria-label={`Manage ${user.email}`} role="group">
      <div className="flex flex-wrap gap-4">
        <div>
          <label htmlFor={`${formId}-role`} className="block text-sm font-medium text-fg-2">
            Role
          </label>
          <select
            id={`${formId}-role`}
            value={role}
            disabled={confirming}
            onChange={(e) => {
              setRole(e.target.value as Role);
              setNote(null);
            }}
            className="mt-1 block min-h-10 rounded-md border border-line-strong bg-surface px-3 text-sm"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${formId}-status`} className="block text-sm font-medium text-fg-2">
            Status
          </label>
          <select
            id={`${formId}-status`}
            value={status}
            disabled={confirming}
            onChange={(e) => {
              setStatus(e.target.value as Status);
              setNote(null);
            }}
            className="mt-1 block min-h-10 rounded-md border border-line-strong bg-surface px-3 text-sm"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-xs text-muted">
        The Bekebi User ID ({user.bekebiUserId}) is assigned by the portal and cannot be changed.
      </p>
      <section aria-label="Sign-in" className="border-t border-line pt-4">
        <h3 className="text-sm font-semibold text-fg">Sign-in</h3>
        <p className="mt-1 text-xs text-muted">
          You never set or see anyone&rsquo;s password. Links go to the user&rsquo;s own email
          address and work once.
        </p>
        <div className="mt-3">
          <SignInLinks user={user} isSelf={isSelf} />
        </div>
      </section>

      {note ? (
        <p role="status" className="text-sm text-fg-2">
          {note}
        </p>
      ) : null}

      {confirming ? (
        <div
          role="group"
          aria-label={`Confirm changes to ${user.email}`}
          className="rounded-md border border-warning-line bg-warning-soft p-3"
        >
          <p className="text-warning">
            Change {user.email}&rsquo;s {changes.join(' and ')}? They will be signed out of the
            portal everywhere and must sign in again.
          </p>
          {isSelf ? (
            <p className="mt-2 font-medium text-warning">
              This is your own account: you will be signed out as soon as it is saved.
            </p>
          ) : null}
          {update.isError ? (
            <p role="alert" className="mt-2 text-danger">
              {update.error.message}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button disabled={update.isPending} onClick={save}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              ref={backRef}
              variant="secondary"
              onClick={() => {
                update.reset();
                setConfirming(false);
              }}
            >
              Back
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button onClick={review}>Review changes</Button>
          <Button variant="secondary" onClick={() => onDone(null)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
